<script lang="ts">
  import { onMount } from "svelte";
  import { get } from "svelte/store";
  import Router from "svelte-spa-router";
  import { push } from "svelte-spa-router";
  import { _ } from "svelte-i18n";
  import { router } from "svelte-spa-router";
  import AppShell from "$components/AppShell.svelte";
  import TermsGate from "$components/TermsGate.svelte";
  import CookieConsent from "$components/CookieConsent.svelte";
  import { canRoute } from "$lib/permissions";
  import { routes } from "./routes";
  import { AUTH_REQUIRED_EVENT } from "$lib/api";
  import { auth, clearSession, loadSession } from "$lib/auth";

  const routeParts = $derived(router.location.split('/'));
  const routeTenant = $derived(routeParts[1] === 't' ? routeParts[2] : '');
  const routeAllowed = $derived(!routeTenant || canRoute($auth,routeTenant,routeParts[3] || '',routeParts[4] || ''));

  function isProtectedRoute(hash: string): boolean {
    return (
      hash.startsWith("#/t/") ||
      hash.startsWith("#/backoffice") ||
      hash.startsWith("#/admin-portal")
    );
  }

  function redirectToLogin(): void {
    const current = window.location.hash || "#/";
    if (current.startsWith("#/login")) return;
    const next = current.replace(/^#/, "") || "/";
    push(`/login?reason=session-expired&next=${encodeURIComponent(next)}`);
  }

  onMount(() => {
    let destroyed = false;
    async function boot() {
      await loadSession();
      if (destroyed) return;
      if (!get(auth).authenticated && isProtectedRoute(window.location.hash)) {
        redirectToLogin();
      }
    }

    function onAuthRequired() {
      clearSession();
      redirectToLogin();
    }

    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    boot();
    return () => {
      destroyed = true;
      window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    };
  });
</script>

{#if $auth.authenticated && $auth.user && !$auth.user.terms_accepted_at}
  <TermsGate />
{/if}

<!-- The term recap screen (owner only), loaded lazily to keep it out of the main bundle. -->
{#if $auth.authenticated}{#await import("$components/TermRecap.svelte") then recap}<recap.default slug={routeTenant} />{/await}{/if}

{#if !$auth.loading}
  <CookieConsent />
{/if}

{#if $auth.loading}
  <div class="boot-spinner" aria-label={$_("common.loading")}>
    <div class="spinner"></div>
  </div>
{:else if !routeAllowed}
  <AppShell tenantSlug={routeTenant} active=""><p role="alert">{$_("permissions.accessDenied")}</p><a href={`#/t/${routeTenant}/dashboard`}>{$_("nav.dashboard")}</a></AppShell>
{:else}
  {#key $auth.memberships.find(m=>m.tenant.slug===routeTenant)?.permission_revision}
    <Router {routes} />
  {/key}
{/if}

<style>
  :global(html) { background: var(--color-bg); }

  .boot-spinner {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg);
  }
  .spinner {
    width: 40px; height: 40px;
    border: 3px solid var(--color-divider);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
